import { Component } from 'react';
import lodash from 'lodash';
import { GridPanel, Grid } from 'common/components/layout/GridPanel';
import { DataGrid } from 'common/components/DataGrid/DataGrid';
import QuickMenu from 'common/components/QuickMenu/QuickMenu';
import icons from 'styles/icons/icons.scss';
import userPermissionsEditorShape from 'binder/components/UserPermissionsEditor/userPermissionsEditorShape';
import styles from 'binder/components/UserPermissionsEditor/ChangePermissionsPanel/ChangePermissionsPanel.scss';

const mixed = '__mixed__';

export class ChangePermissionsPanel extends Component {
    handleChange(name, value) {
        this.props.changePermissionsDelta(name, value);
    }

    handleRevokeAccess() {
        const { selectedUsers, currentUserSub } = this.props;
        if (selectedUsers.includes(currentUserSub)) {
            this.props.addApplicationMessage('warning',
                'You have removed yourself from the binder. \nDo not save these changes unless you want to be removed from the binder.');
        }
        this.props.revokeAccess();
    }

    updatePermissionItems(permissionItems, permissionsDelta) {
        if (lodash.isNil(permissionItems)) {
            return [];
        }
        let atLeastOneHasDelta = false;
        let atLeastOneMixed = false;

        const { selectedUsers, users } = this.props;
        const selectedUserPermissions = selectedUsers.length === 1 ? users[selectedUsers[0]].binderPermissions : {};
        this.permissionItemsData = lodash.map(permissionItems, permissionItem => {
            const { name } = permissionItem;
            const permission = selectedUserPermissions[name];
            const customValue = permission && permission.custom ? permission.value : null;
            const handleChange = this.handleChange.bind(this, name);
            const hasDelta = permissionsDelta[name] || customValue;
            atLeastOneHasDelta = atLeastOneHasDelta || hasDelta;
            atLeastOneMixed = atLeastOneMixed || permissionItem.value === mixed;
            return {
                permissionItem,
                handleChange,
                hasDelta,
            };
        });

        this.atLeastOneHasDelta = atLeastOneHasDelta;
        this.atLeastOneMixed = atLeastOneMixed;
    }

    render() {
        const {
            selectedUsers,
            grantAccess,
            isSaving,
            permissionItems,
            permissionsDelta,
        } = this.props;
        this.updatePermissionItems(permissionItems, permissionsDelta);
        const {
            permissionItemsData,
        } = this;

        if (selectedUsers.length === 0) {
            return (
                <Grid
                    className={styles.changePermissionsPanel}
                    gridRows={1}
                    gridColumns={1}
                    display='grid'>
                    <GridPanel
                        className={styles.emptySelectionGridPanel}
                        gridRow={1}
                        gridColumn={1}
                        rowAlign='center'
                        columnAlign='center'>
                        Please select users to modify.
                    </GridPanel>
                </Grid>
            );
        }

        const grantAccessEnabled = !isSaving && selectedUsers.length > 0;
        const revokeAccessEnabled = !isSaving && selectedUsers.length > 0;

        const headerText = selectedUsers.length === 1
            ? 'Modify Permissions for selected user'
            : `Modify Permissions for all ${selectedUsers.length} users`;

        return (
            <Grid
                className={styles.changePermissionsPanel}
                gridRows={['auto', 1, 'auto']}
                gridColumns={1}
                display='grid'>
                <GridPanel
                    className={styles.changePermissionsHeader}
                    gridRow={1}
                    gridColumn={1}>
                    {headerText}
                </GridPanel>
                <GridPanel gridRow={2} gridColumn={1}>
                    <PermissionsSection
                        permissionItems={permissionItemsData}/>
                </GridPanel>
                <GridPanel gridRow={3} gridColumn={1}>
                    <AccessButtons
                        revokeAccessEnabled={revokeAccessEnabled}
                        revokeAccess={this.handleRevokeAccess.bind(this)}
                        grantAccessEnabled={grantAccessEnabled}
                        grantAccess={grantAccess.bind(null, permissionItems)}/>
                </GridPanel>
            </Grid>
        );
    }
}

export const PermissionsSection = ({
    permissionItems,
}) => {
    const fields = [{
        fieldKey: 'displayName',
        valueSelector: 'permissionItem.displayName',
        contentTemplate: <PermissionText/>,
        length: 1,
        displayOrder: 1,
    }, {
        fieldKey: 'actions',
        valueSelector: 'permissionItem.value',
        contentTemplate: <ChangeMenu/>,
        length: 'auto',
        displayOrder: 2,
        contentRowAlign: 'end',
    }];

    return (
        <div className={styles.changePermissionsDataGridContainer} data-cy="PermissionsSection">
            <DataGrid
                className={styles.changePermissionsDataGrid}
                data={permissionItems}
                fields={fields}
                showHeaders={false}/>
        </div>
    );
};

const PermissionText = ({ value: displayName, rawData }) => {
    const { permissionItem, hasDelta } = rawData;
    const { isRestricted, allRestricted } = permissionItem;
    let tooltip;
    if (allRestricted) {
        tooltip = 'All selected users are restricted; the value cannot be changed.';
    } else if (isRestricted) {
        tooltip = 'At least one selected user is restricted; the value will not be changed for those users';
    }
    const lockSymbol = isRestricted
        ? <span
            title={tooltip}
            className={icons.materialIcons + ' ' + icons.smaller}>lock</span>
        : null;

    const permissionSelectClass = hasDelta
        ? styles.permissionNameDelta
        : styles.permissionName;

    return (
        <div className={styles.permissionsItemLabel} data-cy="PermissionText">
            <span className={permissionSelectClass}>{displayName}</span>
            {lockSymbol}
        </div>
    );
};

const ChangeMenu = ({ value, rawData }) => {
    const { handleChange, hasDelta } = rawData;
    const qmOptions = [{
        label: 'Yes to selected users',
        handler: lodash.partial(handleChange, 'yes'),
    }, {
        label: 'No to selected users',
        handler: lodash.partial(handleChange, 'no'),
    }];

    const headerText = hasDelta ? value : 'actions';
    return <QuickMenu headerText={headerText} options={qmOptions}/>;
};

export const AccessButtons = props => {
    const {
        revokeAccessEnabled,
        revokeAccess,
        grantAccessEnabled,
        grantAccess,
    } = props;
    return (
        <section className={styles.changePermissionsButtons} data-cy="AccessButtons">
            <button
                className={styles.changePermissionsButton}
                disabled={!revokeAccessEnabled}
                onClick={revokeAccess}
                data-cy="ChangePermissionsPanel-Revoke">Revoke Access
                
            </button>
            <button
                className={styles.changePermissionsButton}
                disabled={!grantAccessEnabled}
                onClick={grantAccess}
                data-cy="ChangePermissionsPanel-Change">Change Access
            </button>
        </section>
    );
};

ChangePermissionsPanel.propTypes = userPermissionsEditorShape;

export default ChangePermissionsPanel;
